import type { PathNode, ShpeshftDocument, VectorObject } from '../core/document';

export type CopyComplexity = Readonly<{
  objects:number; paths:number; nodes:number; compoundPaths:number; approximateBytes:number;
}>;

export const COPY_SAFE_LIMITS={objects:1200,nodes:14000,compoundPaths:1800,approximateBytes:10_000_000} as const;
export const COPY_BORDERLINE_LIMITS={objects:750,nodes:9000,compoundPaths:1000,approximateBytes:6_000_000} as const;

const nodeCount=(object:VectorObject)=>object.geometry.kind==='path'
  ?object.geometry.nodes.length+(object.geometry.subpaths??[]).reduce((sum,path)=>sum+path.length,0):0;

export function measureCopyComplexity(document:Pick<ShpeshftDocument,'objects'|'order'>):CopyComplexity{
  let paths=0,nodes=0,compoundPaths=0,approximateBytes=0;
  for(const id of document.order){const object=document.objects[id];if(!object)continue;approximateBytes+=520;if(object.geometry.kind!=='path')continue;paths++;const subpaths=object.geometry.subpaths?.length??0;compoundPaths+=subpaths;const count=nodeCount(object);nodes+=count;approximateBytes+=count*176+subpaths*32;}
  return{objects:document.order.length,paths,nodes,compoundPaths,approximateBytes};
}

export function copySafety(stats:CopyComplexity):'safe'|'borderline'|'unsafe'{
  if(stats.objects>COPY_SAFE_LIMITS.objects||stats.nodes>COPY_SAFE_LIMITS.nodes||stats.compoundPaths>COPY_SAFE_LIMITS.compoundPaths||stats.approximateBytes>COPY_SAFE_LIMITS.approximateBytes)return'unsafe';
  if(stats.objects>COPY_BORDERLINE_LIMITS.objects||stats.nodes>COPY_BORDERLINE_LIMITS.nodes||stats.compoundPaths>COPY_BORDERLINE_LIMITS.compoundPaths||stats.approximateBytes>COPY_BORDERLINE_LIMITS.approximateBytes)return'borderline';
  return'safe';
}

const distance=(a:PathNode,b:PathNode)=>Math.hypot(a.anchor.x-b.anchor.x,a.anchor.y-b.anchor.y);
const rounded=(value:number,precision:number)=>Math.round(value/precision)*precision;
const quantizeNode=(node:PathNode,precision:number):PathNode=>({...node,anchor:{x:rounded(node.anchor.x,precision),y:rounded(node.anchor.y,precision)},in:node.in?{x:rounded(node.in.x,precision),y:rounded(node.in.y,precision)}:null,out:node.out?{x:rounded(node.out.x,precision),y:rounded(node.out.y,precision)}:null});
const redundantCorner=(a:PathNode,b:PathNode,c:PathNode,tolerance:number)=>{
  if(a.in||a.out||b.in||b.out||c.in||c.out)return false;
  const ab={x:b.anchor.x-a.anchor.x,y:b.anchor.y-a.anchor.y},bc={x:c.anchor.x-b.anchor.x,y:c.anchor.y-b.anchor.y},length=Math.hypot(ab.x,ab.y)+Math.hypot(bc.x,bc.y);
  return length>0&&Math.abs(ab.x*bc.y-ab.y*bc.x)/length<tolerance;
};
function optimiseLoop(input:readonly PathNode[],tolerance:number){
  if(input.length<4)return input.map(node=>quantizeNode(node,.01));
  const close=Math.max(.05,tolerance*.4),deduped:PathNode[]=[];
  for(const node of input)if(!deduped.length||distance(deduped[deduped.length-1],node)>close)deduped.push(node);
  if(deduped.length>3&&distance(deduped[0],deduped[deduped.length-1])<=close)deduped.pop();
  let current=deduped,changed=true;
  while(changed&&current.length>3){changed=false;const next=current.filter((node,index)=>{const remove=redundantCorner(current[(index-1+current.length)%current.length],node,current[(index+1)%current.length],tolerance);changed ||= remove;return!remove;});current=next;}
  return current.map(node=>quantizeNode(node,.01));
}
export function optimiseCopyObjects(objects:readonly VectorObject[],tolerance=.18):VectorObject[]{
  return objects.map(object=>object.geometry.kind!=='path'?object:{...object,geometry:{...object.geometry,nodes:optimiseLoop(object.geometry.nodes,tolerance),...(object.geometry.subpaths?{subpaths:object.geometry.subpaths.map(path=>optimiseLoop(path,tolerance))}:{})}});
}

